import React, { useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaUserPlus, FaUserMinus, FaShare, FaSearch, FaFilter, FaTimes, FaUsers, FaChevronDown, FaEye, FaEnvelope, FaBuilding, FaBriefcase } from "react-icons/fa";
import { useToast } from "../../context/ToastContext";
import SkeletonLoader from "../../components/ui/SkeletonLoader";
import EmptyState from "../../components/ui/EmptyState";
import PageLayout from "../../components/ui/PageLayout";
import PageHeader from "../../components/ui/PageHeader";

const EmployeeTable = () => {
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveClientModal, setShowRemoveClientModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingEmployeeDetails, setViewingEmployeeDetails] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", email: "" });
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
    fetchClients();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, employeeSearchTerm, clientSearchTerm, productFilter]);


  const fetchEmployees = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch employees");

      const data = await response.json();
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch clients");

      const data = await response.json();
      setClients(data);
    } catch (error) {
      setError(error.message);
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];

    // Filter by employee name
    if (employeeSearchTerm) {
      const term = employeeSearchTerm.toLowerCase();
      filtered = filtered.filter(employee =>
        employee.name.toLowerCase().includes(term)
      );
    }

    // Filter by client name
    if (clientSearchTerm) {
      const term = clientSearchTerm.toLowerCase();
      filtered = filtered.filter(employee =>
        employee.clients.some(client =>
          client.name.toLowerCase().includes(term)
        )
      );
    }

    // Filter by product
    if (productFilter !== "all") {
      filtered = filtered.filter(employee => {
        if (productFilter === "taxFiling") {
          return employee.clients.some(client => client.isTaxFilingEnabled);
        } else if (productFilter === "bookkeeping") {
          return employee.clients.some(client => client.isBookkeepingEnabled);
        }
        return true;
      });
    }

    setFilteredEmployees(filtered);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setEditFormData({ name: employee.name, email: employee.email });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/${selectedEmployee._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) throw new Error("Failed to update employee");

      const updatedEmployee = await response.json();
      setEmployees(employees.map((emp) => (emp._id === updatedEmployee.employee._id ? updatedEmployee.employee : emp)));
      setShowEditModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/${employeeId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to delete employee");

        setEmployees(employees.filter((emp) => emp._id !== employeeId));
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleAssignClient = (employee) => {
    setSelectedEmployee(employee);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/assign-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ employeeId: selectedEmployee._id, clientId: selectedClient }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to assign client");
      }

      const data = await response.json();
      setEmployees(employees.map((emp) => (emp._id === data.employee._id ? data.employee : emp)));
      setShowAssignModal(false);
      setSelectedClient(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRemoveClient = (employee) => {
    setSelectedEmployee(employee);
    setShowRemoveClientModal(true);
  };

  const handleRemoveClientSubmit = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/remove-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ employeeId: selectedEmployee._id, clientId: selectedClient }),
      });

      if (!response.ok) throw new Error("Failed to remove client");

      const data = await response.json();
      setEmployees(employees.map((emp) => (emp._id === data.employee._id ? data.employee : emp)));
      setShowRemoveClientModal(false);
      setSelectedClient(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const toast = useToast();
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Function to copy the link to the clipboard
  const handleShare = () => {
    const link = "http://employee.bookscomply.com/";
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
        toast.error("Failed to copy link.");
      });
  };

  return (
    <PageLayout>
      <PageHeader
        title="Employee Management"
        description={`${filteredEmployees.length} employees found`}
        icon={FaUsers}
        actions={
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200"
          >
            <FaShare className="w-4 h-4" />
            Add Employee
          </button>
        }
      />

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by employee name"
            value={employeeSearchTerm}
            onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
          />
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client name"
            value={clientSearchTerm}
            onChange={(e) => setClientSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-all"
          >
            <FaFilter className="w-4 h-4" />
            <span>Products: {productFilter === "all" ? "All" : productFilter === "taxFiling" ? "Tax Filing" : "Bookkeeping"}</span>
            <FaChevronDown className={`w-3 h-3 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showFilterDropdown && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl shadow-xl z-20 overflow-hidden animate-fade-in">
              {[
                { value: 'all', label: 'All Products' },
                { value: 'taxFiling', label: 'Tax Filing' },
                { value: 'bookkeeping', label: 'Bookkeeping' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => { setProductFilter(option.value); setShowFilterDropdown(false); }}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${productFilter === option.value
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <SkeletonLoader variant="table" rows={6} cols={4} />
      ) : error ? (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400">
          {error}
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-dark-700/50 text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-dark-700">
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Sr No.</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Assignments</th>
                  <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {filteredEmployees.map((employee, index) => (
                  <tr key={employee._id} className="group hover:bg-gray-50/50 dark:hover:bg-dark-700/30 transition-all duration-200">
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 font-medium">{index + 1}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center font-bold shadow-md shadow-primary-500/20">
                          {employee.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900 dark:text-white leading-tight text-base">{employee.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-1">
                            <FaEnvelope className="w-3 h-3 opacity-60" /> {employee.email}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-bold rounded-lg border border-primary-100 dark:border-primary-900/30 flex items-center gap-1.5">
                            <FaBuilding className="w-3 h-3" /> {employee.clients.length} Clients
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {Array.from(new Set(employee.clients.flatMap(c => {
                            const services = [];
                            if (c.isTaxFilingEnabled) services.push("Tax Filing");
                            if (c.isBookkeepingEnabled) services.push("Bookkeeping");
                            return services;
                          }))).map(service => (
                            <span key={service} className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold rounded-md uppercase tracking-wide border border-gray-200 dark:border-dark-600">
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => { setViewingEmployeeDetails(employee); setShowDetailsModal(true); }}
                          className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all hover:scale-110"
                          title="View Details"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                        <div className="w-px h-6 bg-gray-200 dark:bg-dark-700 mx-1" />
                        <button onClick={() => handleEdit(employee)} className="p-2 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-all hover:scale-110" title="Edit Info">
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleAssignClient(employee)} className="p-2 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-all hover:scale-110" title="Assign Client">
                          <FaUserPlus className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleRemoveClient(employee)} className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all hover:scale-110" title="Remove Client">
                          <FaUserMinus className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(employee._id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all hover:scale-110" title="Delete Employee">
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredEmployees.length === 0 && (
            <EmptyState
              icon={FaUsers}
              preset="search"
              title="No Employees Found"
              description="Try adjusting your search or filters"
            />
          )}
        </div>
      )}

      {showEditModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content !max-w-md animate-bounce-in">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Employee</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                <input type="text" value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} required className="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input type="email" value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} required className="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500" />
              </div>
              <button type="submit" className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all">Save Changes</button>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Assign Client Modal */}
      {showAssignModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content !max-w-md animate-bounce-in">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Assign Client</h3>
              <button onClick={() => setShowAssignModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Client</label>
                <select value={selectedClient || ""} onChange={(e) => setSelectedClient(e.target.value)} className="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500">
                  <option value="">Select a client</option>
                  {clients.filter((client) => !employees.some((emp) => emp.clients.some((c) => c._id === client._id))).map((client) => (
                    <option key={client._id} value={client._id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowAssignModal(false)} className="flex-1 py-3 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors">Close</button>
                <button onClick={handleAssignSubmit} className="flex-1 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all">Assign</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Remove Client Modal */}
      {showRemoveClientModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content !max-w-md animate-bounce-in">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Remove Client</h3>
              <button onClick={() => setShowRemoveClientModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Client</label>
                <select value={selectedClient || ""} onChange={(e) => setSelectedClient(e.target.value)} className="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500">
                  <option value="">Select a client</option>
                  {selectedEmployee?.clients.map((client) => (
                    <option key={client._id} value={client._id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowRemoveClientModal(false)} className="flex-1 py-3 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors">Close</button>
                <button onClick={handleRemoveClientSubmit} className="flex-1 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all">Remove</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {showDetailsModal && viewingEmployeeDetails && createPortal(
        <div className="modal-overlay">
          <div className="modal-content !max-w-4xl overflow-hidden animate-zoom-in border border-white/20">
            <div className="p-8 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-gradient-to-r from-gray-50/50 to-transparent dark:from-dark-700/30">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-primary-600 text-white flex items-center justify-center text-2xl font-bold shadow-xl shadow-primary-500/30">
                  {viewingEmployeeDetails.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{viewingEmployeeDetails.name}</h2>
                  <p className="text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1">
                    <FaEnvelope className="w-3.5 h-3.5" />
                    {viewingEmployeeDetails.email}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="p-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-all bg-gray-100 dark:bg-dark-700 rounded-xl hover:rotate-90">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest flex items-center gap-2">
                  <FaBriefcase className="w-4 h-4" />
                  Assigned Clients ({viewingEmployeeDetails.clients.length})
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {viewingEmployeeDetails.clients.length > 0 ? viewingEmployeeDetails.clients.map(client => (
                  <div key={client._id} className="p-4 bg-gray-50 dark:bg-dark-700/40 rounded-xl border border-gray-150 dark:border-dark-700 flex items-center justify-between group hover:border-primary-500/50 hover:bg-white dark:hover:bg-dark-700 transition-all duration-300 shadow-sm">
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 flex items-center justify-center text-gray-400 group-hover:text-primary-500 transition-all duration-300">
                        <FaBuilding className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-gray-800 dark:text-gray-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-all">{client.name}</h4>
                        <div className="flex gap-2 mt-1.5">
                          {client.isTaxFilingEnabled && (
                            <span className="text-[9px] px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-md font-bold tracking-wider transition-all">TAX FILING</span>
                          )}
                          {client.isBookkeepingEnabled && (
                            <span className="text-[9px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-md font-bold tracking-wider transition-all">BOOKKEEPING</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-12 bg-gray-50 dark:bg-dark-700/20 rounded-2xl border border-dashed border-gray-200 dark:border-dark-700">
                    <FaUsers className="w-12 h-12 text-gray-200 dark:text-dark-700 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">No clients assigned to this employee.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-dark-700/30 border-t border-gray-100 dark:border-dark-700 flex justify-end">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2.5 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 font-bold rounded-xl border border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-all shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </PageLayout>
  );
};

export default EmployeeTable;